import AgricultureIcon from "@mui/icons-material/Agriculture";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GrassIcon from "@mui/icons-material/Grass";
import MapIcon from "@mui/icons-material/Map";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import { Link } from "react-router-dom";

/**
 * Simplified welcome screen shown after signup.
 *
 * This is a brief introduction that encourages users to get started.
 * Detailed guidance is provided through the OnboardingChecklist component
 * which appears after this screen.
 */
export default function Welcome() {
  const { t } = useTranslation();

  return (
    <>
      <DialogTitle id="dialog-title" sx={{ textAlign: "center", pb: 1 }}>
        {t("signup.welcome.newTitle")}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          sx={{ textAlign: "center", mb: 3, color: "text.secondary" }}
        >
          {t("signup.welcome.newSubtitle")}
        </Typography>

        {/* Quick overview of what they'll do */}
        <Box sx={{ mb: 3 }}>
          <StepPreview>
            <StepIcon>
              <MapIcon color="primary" />
            </StepIcon>
            <Typography variant="body2">{t("signup.welcome.step1")}</Typography>
          </StepPreview>

          <StepPreview>
            <StepIcon>
              <GrassIcon color="primary" />
            </StepIcon>
            <Typography variant="body2">{t("signup.welcome.step2")}</Typography>
          </StepPreview>

          <StepPreview>
            <StepIcon>
              <AgricultureIcon color="primary" />
            </StepIcon>
            <Typography variant="body2">{t("signup.welcome.step3")}</Typography>
          </StepPreview>

          <StepPreview>
            <StepIcon>
              <CheckCircleOutlineIcon color="primary" />
            </StepIcon>
            <Typography variant="body2">{t("signup.welcome.step4")}</Typography>
          </StepPreview>
        </Box>

        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "text.secondary" }}
        >
          {t("signup.welcome.helpText")}{" "}
          <MuiLink href="mailto:support@ruumi.io" underline="hover">
            {t("signup.welcome.link")}
          </MuiLink>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Button
          component={Link}
          to="/add-farm/enter-name"
          color="primary"
          variant="contained"
          size="large"
        >
          {t("signup.button.getStarted")}
        </Button>
      </DialogActions>
    </>
  );
}

const StepPreview = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StepIcon = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main + "14",
  flexShrink: 0,
}));
